import React, { useState, useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ChevronsUpDown, Check, Search } from 'lucide-react';

function MasterCombobox({
  items = [],
  value,
  selectedId,
  onSelect,
  placeholder = "Select...",
  testId,
  displayField = "name",
  subtextField = null,
  disabled = false,
  className = ""
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const activeId = value !== undefined && value !== null && value !== '' ? value : (selectedId || '');
  const selected = items.find(item => String(item.id) === String(activeId));

  const filteredItems = useMemo(() => {
    if (!search) return items;
    const s = search.toLowerCase();
    return items.filter(item => {
      const main = String(item[displayField] || '').toLowerCase();
      const sub = subtextField && item[subtextField] ? String(item[subtextField]).toLowerCase() : '';
      return main.includes(s) || sub.includes(s);
    });
  }, [items, search, displayField, subtextField]);

  const handleSelect = (itemId) => {
    if (onSelect) {
      onSelect(itemId);
    }
    setOpen(false);
    setSearch('');
  };

  return (
    <Popover open={open} onOpenChange={(isOpen) => { if (!disabled) { setOpen(isOpen); if (!isOpen) setSearch(''); } }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testId}
          className={`w-full justify-between border-gray-200 font-ibm font-normal text-xs h-9 hover:bg-gray-50 bg-white ${disabled ? 'opacity-60 cursor-not-allowed bg-gray-50' : ''} ${className}`}
        >
          <span className="truncate text-left text-slate-800">
            {selected ? selected[displayField] : <span className="text-gray-400">{placeholder}</span>}
          </span>
          <ChevronsUpDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50 text-gray-500" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[240px] p-0 shadow-lg border border-gray-200 bg-white z-50" align="start">
        <div className="flex items-center border-b border-gray-100 px-3 py-2 bg-gray-50/50">
          <Search className="mr-2 h-3.5 w-3.5 shrink-0 opacity-50 text-gray-500" />
          <Input 
            placeholder="Search..." 
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border-0 h-7 text-xs focus-visible:ring-0 focus-visible:ring-offset-0 px-0 bg-transparent font-ibm"
            autoFocus
          />
        </div>
        <div className="max-h-[220px] overflow-y-auto p-1 divide-y divide-gray-50">
          {filteredItems.length === 0 ? (
            <div className="py-5 text-center text-xs text-muted-foreground font-ibm">No results found.</div>
          ) : (
            filteredItems.map(item => {
              const isSelected = String(activeId) === String(item.id);
              return (
                <div
                  key={item.id}
                  onClick={() => handleSelect(item.id)}
                  className={`relative flex cursor-pointer select-none items-center rounded-md px-2.5 py-1.5 text-xs outline-none transition-colors ${
                    isSelected ? 'bg-blue-50 text-accent-blue font-semibold' : 'hover:bg-gray-100 text-slate-700'
                  }`}
                >
                  <Check className={`mr-2 h-3.5 w-3.5 shrink-0 ${isSelected ? "opacity-100 text-accent-blue" : "opacity-0"}`} />
                  <div className="flex flex-col min-w-0">
                    <span className="truncate font-medium">{item[displayField]}</span>
                    {subtextField && item[subtextField] && (
                      <span className="text-[11px] text-gray-400 truncate">{item[subtextField]}</span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default MasterCombobox;
